import { resolveStoredFileUrl } from "utils/storedFileUrl";
// @mui material components
import { Card, Fade, Grid, Icon, Modal, Tooltip } from "@mui/material";
import PropTypes from "prop-types";

// Kaizen Dashboard components
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import DocumentacionItem from "../DocumentacionItem";
import { useContext, useEffect, useRef, useState } from "react";
import JSZip, { file } from "jszip";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import { MessageManager } from "context";
import { formatDate } from "utils/formatters";
import { saveAs } from "file-saver";
import SinDatos from "components/Carteles/SinDatos";
import dayjs from "dayjs";
import ModalVencimientoDocumentacion from "../VencimientoDocumentacion";
import ModalRechazo from "../ModalRechazo";
import { ConfirmarContext } from "context/ConfirmarContext";
import { UserContext } from "context/user";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "90%", xxl: "40%" },
  overflow: "auto",
};

export default function ModalDocumentacionProveedor({ open, handleClose, idProveedor, readOnly }) {
  const { handleSnackbar } = useContext(MessageManager);
  const { confirmar } = useContext(ConfirmarContext);
  const { verificarAcceso } = useContext(UserContext);
  const fileRef = useRef();
  const [files, setFiles] = useState([]);
  const [fechaVencimiento, setFechaVencimiento] = useState("");
  const [file, setFile] = useState(null);
  const [openModalVencimiento, setOpenModalVencimiento] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  const [openModalRechazo, setOpenModalRechazo] = useState(false);
  const [filePendienteAprobacion, setFilePendienteAprobacion] = useState(null);
  const [fileAEditar, setFileAEditar] = useState(null);
  const [openRequisitos, setOpenRequisitos] = useState(false);
  const [getProveedor] = useLazyQuery(
    gql`
      query Proveedor($id: ID) {
        proveedores(filter: { id: $id }) {
          documentos(estado: ["Pendiente", "Aprobado"]) {
            id
            nombre
            file
            fechaVencimiento
            createdAt
            descripcion
            estado
            comentario
            user {
              id
              nombre
              apellido
              username
            }
          }
        }
      }
    `,
    {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        if (!data) return;
        let proveedor = data.proveedores[0];
        if (proveedor) {
          setFiles(proveedor.documentos);
        }
      },
    }
  );

  const [saveFile, { loading: loadingSave }] = useMutation(
    gql`
      mutation addProveedorFile(
        $idProveedor: ID!
        $file: Upload!
        $fechaVencimiento: Date
        $descripcion: String
      ) {
        addProveedorFile(
          idProveedor: $idProveedor
          file: $file
          fechaVencimiento: $fechaVencimiento
          descripcion: $descripcion
          estado: "Aprobado"
        ) {
          id
        }
      }
    `,
    {
      onCompleted: () => {
        handleSnackbar("Archivo subido correctamente", "success");
        getProveedor({ variables: { id: idProveedor } });
      },
      onError: (error) => {
        handleSnackbar(error.message, "error");
      },
    }
  );

  const [updateProveedorFileStatus, { loading: loadingAprobar }] = useMutation(
    gql`
      mutation updateProveedorFileStatus($id: ID!, $estado: String!, $comentario: String, $fechaVencimiento: Date) {
        updateProveedorFileStatus(id: $id, estado: $estado, comentario: $comentario, fechaVencimiento: $fechaVencimiento) {
          id
        }
      }
    `
  );

  const [deleteFile, { loading: loadingDelete }] = useMutation(
    gql`
      mutation deleteProveedorFile($id: ID!) {
        deleteProveedorFile(id: $id)
      }
    `,
    {
      onCompleted: () => {
        handleSnackbar("Archivo eliminado correctamente", "success");
        getProveedor({ variables: { id: idProveedor } });
      },
      onError: (error) => {
        handleSnackbar(error.message, "error");
      },
    }
  );

  useEffect(() => {
    if (open && idProveedor) {
      getProveedor({ variables: { id: idProveedor } });
    } else {
      setFiles([]);
    }
  }, [open]);

  const handleDownloadAll = async () => {
    const zip = new JSZip();
    let filesFiltrados = files.filter((file) =>
      dayjs(file.fechaVencimiento).isAfter(dayjs().subtract(1, "day"))
    );

    for (let file of filesFiltrados) {
      const res = await fetch(resolveStoredFileUrl(file.file));
      let filename = file.file.split("\\").pop();
      const blob = await res.blob();
      zip.file(filename, blob);
    }

    zip.generateAsync({ type: "blob" }).then((content) => {
      saveAs(content, "documentacion.zip");
    });
  };

  const handleEditarFechaVencimiento = (file) => {
    setFileAEditar(file);
    setFechaVencimiento(file.fechaVencimiento || "");
    setOpenModalVencimiento(true);
  };

  return (
    <>
      <Modal
        aria-labelledby="requisitos-modal-title"
        open={openRequisitos}
        onClose={() => setOpenRequisitos(false)}
        closeAfterTransition
      >
        <Fade in={openRequisitos}>
          <Card sx={style}>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" p={3} pb={0}>
              <SoftTypography variant="h6">Requisitos Obligatorios</SoftTypography>
              <SoftBox>
                <Tooltip title="Cerrar" placement="top">
                  <SoftButton
                    variant="gradient"
                    color="error"
                    iconOnly
                    circular
                    onClick={() => setOpenRequisitos(false)}
                  >
                    <Icon sx={{ fontWeight: "regular" }}>close</Icon>
                  </SoftButton>
                </Tooltip>
              </SoftBox>
            </SoftBox>
            <SoftBox p={3}>
              <Grid container spacing={1} component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
                {[
                  {
                    nombre: "Constancia de inscripción AFIP",
                    obligatorio: "Sí",
                  },
                  {
                    nombre: "Constancia de inscripción IIBB",
                    obligatorio: "Sí",
                  },
                  {
                    nombre: "Certificado de situación fiscal regular unificado",
                    obligatorio: "Sí",
                  },
                  {
                    nombre: "Contrato Social (persona jurídica)",
                    obligatorio: "Solo si es Jurídica",
                  },
                  {
                    nombre: "Constancia de CBU",
                    obligatorio: "Sí",
                  },
                ].map((req, index) => (
                  <Grid
                    item
                    xs={12}
                    key={index}
                    component="li"
                    sx={{
                      borderBottom: index !== 4 ? "1px solid" : "none", 
                      borderColor: "grey.300",
                    }}
                  >
                    <SoftBox
                      display="grid"
                      gridTemplateColumns={{ xs: "1fr 120px", sm: "1fr 140px" }}
                      gap={2}
                      alignItems="center"
                      p={1}
                      borderRadius="sm"
                      bgcolor={index % 2 === 0 ? "grey.100" : "transparent"}
                    >
                      <SoftTypography variant="button" fontWeight="regular" color="text">
                        {req.nombre}
                      </SoftTypography>
                      <SoftBox display="flex" alignItems="center">
                        <Icon color="success" sx={{ mr: 0.5 }}>
                          check_circle
                        </Icon>
                        <SoftTypography variant="button" fontWeight="regular">
                          {req.obligatorio}
                        </SoftTypography>
                      </SoftBox>
                    </SoftBox>
                  </Grid>
                ))}
              </Grid>
            </SoftBox>
            <SoftBox p={3} pt={0} display="flex" justifyContent="flex-end">
              <SoftButton
                variant="gradient"
                color="dark"
                onClick={() => setOpenRequisitos(false)}
              >
                Entendido
              </SoftButton>
            </SoftBox>
          </Card>
        </Fade>
      </Modal>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={open}
        onClose={handleClose}
        closeAfterTransition
      >
        <Fade in={open}>
          <Card sx={style}>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" p={3} pb={0}>
              <SoftTypography variant="h6">Documentación</SoftTypography>
              <SoftBox display="flex" justifyContent="end" alignItems="center">
                <SoftBox mr={1}>
                  <Tooltip title="Requisitos obligatorios" placement="top">
                    <SoftButton
                      variant="gradient"
                      color="uproYellow"
                      iconOnly
                      circular
                      onClick={() => setOpenRequisitos(true)}
                    >
                      <Icon fontSize="small">rule</Icon>
                    </SoftButton>
                  </Tooltip>
                </SoftBox>
                <SoftBox mr={1}>
                  <Tooltip placement="top" title="Descargar todos">
                    <SoftButton
                      variant="gradient"
                      color="info"
                      iconOnly
                      circular
                      onClick={handleDownloadAll}
                      disabled={files.length < 1}
                    >
                      <Icon sx={{ fontWeight: "regular" }}>download</Icon>
                    </SoftButton>
                  </Tooltip>
                </SoftBox>
                {!readOnly && (
                  <SoftBox mr={1}>
                    <Tooltip placement="top" title="Agregar nuevo">
                      <SoftButton
                        variant="gradient"
                        color="success"
                        disabled={!verificarAcceso(57)}
                        iconOnly
                        circular
                        onClick={() => {
                          fileRef.current.value = null;
                          fileRef.current.click();
                        }}
                      >
                        <Icon sx={{ fontWeight: "regular" }}>add</Icon>
                      </SoftButton>
                    </Tooltip>
                    <input
                      ref={fileRef}
                      type="file"
                      style={{ display: "none" }}
                      accept=".pdf, .docx, .jpg, .jpeg, .png"
                      onChange={(e) => {
                        setFile(e.target.files[0]);
                        setOpenModalVencimiento(true);
                      }}
                    />
                  </SoftBox>
                )}
                <SoftBox>
                  <Tooltip placement="top" title="Cancelar">
                    <SoftButton
                      variant="gradient"
                      color="error"
                      iconOnly
                      circular
                      onClick={handleClose}
                    >
                      <Icon sx={{ fontWeight: "regular" }}>close</Icon>
                    </SoftButton>
                  </Tooltip>
                </SoftBox>
              </SoftBox>
            </SoftBox>
            <SoftBox p={3}>
              <Grid container>
                <Grid item xs={12}>
                  <SoftBox>
                    <SoftBox
                      component="ul"
                      display="flex"
                      flexDirection="column"
                      p={0}
                      m={0}
                      sx={{ listStyle: "none" }}
                    >
                      {files?.map((file, index) => (
                        <DocumentacionItem
                          key={index}
                          index={index}
                          name={file.descripcion || file.nombre}
                          value={file.file}
                          description={
                            file.user.nombre
                              ? `Subido por ${file.user.nombre} ${file.user.apellido
                              } el ${formatDate(file.createdAt, "dd MMMM de yyyy a las hh:mm")}`
                              : `Subido por ${file.user.username} el ${formatDate(
                                file.createdAt,
                                "dd MMMM de yyyy a las hh:mm"
                              )}`
                          }
                          creacion={file.createdAt}
                          vencimiento={file.fechaVencimiento}
                          onDelete={() => {
                            setLoadingId(index);
                            deleteFile({ variables: { id: file.id } });
                          }}
                          readOnly={readOnly}
                          onEdit={() => handleEditarFechaVencimiento(file)}
                          file={file}
                          loading={(loadingAprobar || loadingDelete || loadingSave) && loadingId === index}
                          handleAprobar={() => {
                            setLoadingId(index);
                            if (!file.fechaVencimiento) {
                              setFilePendienteAprobacion(file);
                              setOpenModalVencimiento(true);
                              return;
                            }
                            if (dayjs(file.fechaVencimiento).isBefore(dayjs().subtract(1, "day"))) {
                              confirmar({
                                title: "Documento vencido",
                                message: "El documento está vencido, ¿está seguro que desea aprobarlo?",
                                icon: "warning",
                                func: () => {
                                  updateProveedorFileStatus({
                                    variables: {
                                      id: file.id,
                                      estado: "Aprobado",
                                      comentario: null,
                                    },
                                  }).then(() => {
                                    getProveedor({ variables: { id: idProveedor } });
                                    handleSnackbar("Documento aprobado correctamente", "success");
                                  });
                                },
                              });
                            } else {
                              updateProveedorFileStatus({
                                variables: {
                                  id: file.id,
                                  estado: "Aprobado",
                                  comentario: null,
                                },
                              }).then(() => {
                                getProveedor({ variables: { id: idProveedor } });
                                handleSnackbar("Documento aprobado correctamente", "success");
                              });
                            }
                          }}
                          handleRechazar={() => {
                            setLoadingId(index);
                            setLoadingId(index);
                            setOpenModalRechazo(true);
                          }}
                        />
                      ))}
                      {files.length === 0 && <SinDatos />}
                    </SoftBox>
                  </SoftBox>
                </Grid>
              </Grid>
            </SoftBox>
            <ModalVencimientoDocumentacion
              open={openModalVencimiento}
              handleClose={() => {
                setOpenModalVencimiento(false);
                setFilePendienteAprobacion(null); // limpiar si se cancela
              }}
              fechaVencimiento={fechaVencimiento}
              setFechaVencimiento={setFechaVencimiento}
              handleSave={() => {
                if (filePendienteAprobacion || fileAEditar) {
                  const fileAprobacion = filePendienteAprobacion || fileAEditar;
                  updateProveedorFileStatus({
                    variables: {
                      id: fileAprobacion.id,
                      estado: "Aprobado",
                      fechaVencimiento: dayjs(fechaVencimiento).format("YYYY-MM-DD"),
                      comentario: null,
                    },
                  }).then(() => {
                    getProveedor({ variables: { id: idProveedor } });
                    handleSnackbar("Documento aprobado correctamente", "success");
                    setOpenModalVencimiento(false);
                    setFilePendienteAprobacion(null);
                    setFechaVencimiento("");
                  });
                } else {
                  saveFile({
                    variables: {
                      idProveedor: idProveedor,
                      file: file,
                      fechaVencimiento: dayjs(fechaVencimiento).format("YYYY-MM-DD"),
                      descripcion: file.name,
                    },
                  }).then(() => {
                    setFile(null);
                    setFechaVencimiento("");
                    setOpenModalVencimiento(false);
                  });
                }
              }}
              loading={loadingSave || loadingAprobar}
            />
            <ModalRechazo
              open={openModalRechazo}
              handleClose={() => setOpenModalRechazo(false)}
              handleSave={(comentario) => {
                setLoadingId(null);
                updateProveedorFileStatus({
                  variables: {
                    id: files[loadingId].id,
                    estado: "Rechazado",
                    comentario: comentario,
                  },
                }).then(() => {
                  getProveedor({ variables: { id: idProveedor } });
                  handleSnackbar("Documento rechazado correctamente", "success");
                  setOpenModalRechazo(false);
                });
              }}
            />
          </Card>
        </Fade>
      </Modal>
    </>
  );
}

ModalDocumentacionProveedor.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  idProveedor: PropTypes.string,
  readOnly: PropTypes.bool,
};
